import { Toast } from '@heroui/react';
import imageCompression from 'browser-image-compression';
import pluralize from 'pluralize';

import { MAX_FILE_SIZE_MB, ALLOWED_IMAGE_TYPES } from '@const';
import type { UploadResult, TypedSupabaseClient } from '@models';
import { StorageBuckets } from '@models';
import {
  isRejected,
  isFulfilled,
  isFailedUpload,
  getErrorMessage,
  isSuccessfulUpload,
} from '@utils';

export const getPublicUrl = (bucket: StorageBuckets, path?: string | null) => {
  const projectUrl = SUPABASE_URL.replace(/\/+$/, '');
  const objectPath = (path || 'default.png').replace(/^\/+/, '');

  return encodeURI(
    `${projectUrl}/storage/v1/object/public/${bucket}/${objectPath}`
  );
};

export const createSignedUrls = async (
  supabase: TypedSupabaseClient,
  filePaths: string[],
  expiresIn = 60 * 5
) => {
  const { data, error } = await supabase.storage
    .from(StorageBuckets.OCCURRENCE_PHOTOS)
    .createSignedUrls(filePaths, expiresIn);

  if (error) {
    throw new Error(error.message);
  }

  return data;
};

export const listFiles = async (
  supabase: TypedSupabaseClient,
  bucket: StorageBuckets,
  path: string
) => {
  const { data, error } = await supabase.storage.from(bucket).list(path);

  if (error) {
    throw new Error(error.message);
  }

  return data;
};

export const uploadFile = async (
  supabase: TypedSupabaseClient,
  bucket: StorageBuckets,
  path: string,
  file: File
) => {
  const { data, error } = await supabase.storage
    .from(bucket)
    .upload(path, file);

  if (error) {
    throw new Error(error.message);
  }

  return data.path;
};

export const deleteFile = async (
  supabase: TypedSupabaseClient,
  bucket: StorageBuckets,
  path: string
) => {
  const { error } = await supabase.storage.from(bucket).remove([path]);

  if (error) {
    throw new Error(error.message);
  }

  return true;
};

export const uploadImage = async (
  supabase: TypedSupabaseClient,
  bucket: StorageBuckets,
  file: File,
  userId: string,
  folder?: string
): Promise<UploadResult> => {
  if (!ALLOWED_IMAGE_TYPES[bucket].includes(file.type)) {
    return { error: 'Invalid file type', status: 'error' };
  }

  if (file.size > MAX_FILE_SIZE_MB[bucket] * 1024 * 1024) {
    return {
      error: `File size exceeds ${MAX_FILE_SIZE_MB[bucket]} MB limit`,
      status: 'error',
    };
  }

  const compressedFile = await imageCompression(file, {
    maxSizeMB: 1.5,
    maxWidthOrHeight: 1920,
    useWebWorker: true,
  });

  try {
    const path = await uploadFile(
      supabase,
      StorageBuckets.OCCURRENCE_PHOTOS,
      `${userId}/${folder ? `${folder}/` : ''}${Date.now()}-${compressedFile.name}`,
      compressedFile
    );

    return { path, status: 'success' };
  } catch (error) {
    return { error: getErrorMessage(error), status: 'error' };
  }
};

export const uploadImages = async (
  supabase: TypedSupabaseClient,
  bucket: StorageBuckets,
  userId: string,
  files: File[],
  folder?: string
) => {
  const results = await Promise.allSettled(
    files.map((file) => {
      return uploadImage(supabase, bucket, file, userId, folder);
    })
  );

  const fulfilledUploadResults = results.filter(isFulfilled);
  const rejectedUploadResults = results.filter(isRejected);

  const rejectedUploadReasons = rejectedUploadResults.map((result) => {
    return result.reason;
  });

  const failedUploadErrors = fulfilledUploadResults
    .filter(isFailedUpload)
    .map(({ value }) => {
      return value.error;
    })
    .concat(rejectedUploadReasons);

  if (failedUploadErrors.length) {
    Toast.toast.danger(
      `Failed to upload ${pluralize('photo', failedUploadErrors.length, true)}`,
      { description: `Error details: ${failedUploadErrors.join(', ')}` }
    );
  }

  return fulfilledUploadResults.filter(isSuccessfulUpload).map(({ value }) => {
    return value.path;
  });
};
